/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useState, useEffect, useRef } from 'react'; // NOVO: import useRef
import Dropzone from 'react-dropzone';
import { FieldValues, UseFormSetValue } from 'react-hook-form';
import parse from 'html-react-parser';
import { TrashIcon } from '@heroicons/react/24/outline';

import Paragraph from '../Text/Paragraph';
import Heading from '../Text/Heading';
import Button from '../Form/Button';
import { StyledFileUploader, StyledBackgroundImage } from './styles';
import { convertToBase64 } from '../../pages/Documents/utils';

interface FileUploaderProps {
  name: string;
  title?: string;
  multiple?: boolean;
  thumbs?: boolean;
  imageAsBackground?: boolean;
  onChange: () => void;
  onBlur: () => void;
  setValue: UseFormSetValue<FieldValues>;
  defaultImages?: string[];
}

const acceptedMimeTypes: Record<string, string[]> = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg', '.jpg'],
};

const FileUploader = React.forwardRef<HTMLInputElement, FileUploaderProps>(
  (
    {
      name,
      title = '',
      multiple = true,
      thumbs = true,
      imageAsBackground = false,
      onChange,
      onBlur,
      setValue,
      defaultImages = [],
    },
    ref
  ) => {
    const [images, setImages] = useState<string[]>(defaultImages);
    const [isFocused, setIsFocused] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const onDropFile = useCallback(
      async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) {
          return;
        }

        const files = [];
        for (const file of acceptedFiles) {
          const convertedFile = await convertToBase64(file);
          files.push(
            imageAsBackground ? (convertedFile as string) : `<img alt="${file.name}" src="${convertedFile}"/>`
          );
        }

        const newImages = multiple ? [...images, ...files] : files;
        setImages(newImages);
        setValue(name, newImages);
      },
      [imageAsBackground, multiple, name, setValue, images]
    );

    const removeFile = useCallback(
      (file: string) => () => {
        const newFiles = [...images];
        newFiles.splice(newFiles.indexOf(file), 1);
        setImages(newFiles);
        setValue(name, newFiles);
      },
      [images, name, setValue]
    );

    useEffect(() => {
      const handlePaste = (event: Event) => {
        if (!isFocused) return;
        const clipboardEvent = event as ClipboardEvent;
        const items = clipboardEvent.clipboardData?.items;
        if (!items) return;

        const filesToUpload: File[] = [];

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (acceptedMimeTypes[item.type]) {
            const file = item.getAsFile();
            if (file) {
              filesToUpload.push(file);
            }
          }
        }

        if (filesToUpload.length > 0) {
          event.preventDefault();
          onDropFile(filesToUpload);
        }
      };

      window.addEventListener('paste', handlePaste);

      return () => {
        window.removeEventListener('paste', handlePaste);
      };
    }, [onDropFile, isFocused]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsFocused(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    return (
      <Dropzone noClick multiple={multiple} onDrop={onDropFile} accept={acceptedMimeTypes}>
        {({ getRootProps, getInputProps, open }) => (
          <StyledFileUploader
            ref={containerRef}
            onClick={() => setIsFocused(true)}
            className={`${imageAsBackground ? ' image-as-background' : ''} ${isFocused ? 'focused' : ''}`}>
            <div className="dropzone" data-testid="drop-input" {...getRootProps()}>
              <input
                ref={ref}
                {...getInputProps({
                  id: name,
                  onChange,
                  onBlur,
                })}
              />
              {imageAsBackground && images[0] ? (
                <StyledBackgroundImage background={images[0]} onClick={open} />
              ) : (
                <>
                  {title && <Heading size="medium">{title}</Heading>}
                  <Paragraph>
                    Arraste as imagens, clique na área e cole (Ctrl+V) a imagem ou <a onClick={open}>clique aqui</a>{' '}
                    para fazer upload
                  </Paragraph>
                  <Paragraph size="small">O formato do arquivo deve ser .PNG ou .JPG</Paragraph>
                </>
              )}
            </div>

            {thumbs && (
              <ul className="thumbs">
                {images.map((image) => {
                  const altPattern = /<img.*?alt="(.*?)".*?>/i;
                  const altMatch = image.match(altPattern);
                  const key = altMatch ? altMatch[1] : image;
                  const imageName = altMatch ? altMatch[1] : 'Imagem colada';

                  return (
                    <li key={key}>
                      <div>
                        {parse(image)}
                        <Paragraph size="small">{imageName}</Paragraph>
                      </div>
                      <Button type="button" kind="outline" onClick={removeFile(image)}>
                        <TrashIcon />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </StyledFileUploader>
        )}
      </Dropzone>
    );
  }
);

FileUploader.displayName = 'FileUploader';
export default FileUploader;
